import { useParams, Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { formatRupiah, PURCHASE_TYPE_LABELS } from '../../types'
import { orderApi } from '../../utils/api'

interface OrderDetail {
  id: number
  orderNumber: string
  outletName: string
  totalAmount: number
  status: string
  createdAt: string
  items: {
    productName: string
    purchaseType: string
    quantity: number
    unitPrice: number
    subtotal: number
  }[]
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  CONFIRMED: { label: 'Dikonfirmasi', className: 'badge-info' },
  PRINTING: { label: 'Sedang Cetak', className: 'badge-warning' },
  PRINTED: { label: 'Tercetak', className: 'badge-success' },
  PRINT_FAILED: { label: 'Gagal Cetak', className: 'badge-error' },
}

export function AdminOrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const orderId = parseInt(id || '0', 10)
  const [order, setOrder] = useState<OrderDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!orderId) {
      setError('Pesanan dengan ID tersebut tidak ada')
      setLoading(false)
      return
    }

    const fetchOrder = async () => {
      try {
        setLoading(true)
        setError(null)
        const res = await orderApi.getById(orderId)
        setOrder(res.data as OrderDetail)
      } catch (err) {
        console.error('Failed to fetch order detail:', err)
        setError('Gagal memuat detail pesanan')
        setOrder(null)
      } finally {
        setLoading(false)
      }
    }

    fetchOrder()
  }, [orderId])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner" aria-label="Memuat detail pesanan" />
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="text-center py-12">
        <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Pesanan tidak ditemukan</h2>
        <p className="text-slate-500 mb-4">{error || 'Pesanan dengan ID tersebut tidak ada'}</p>
        <Link to="/admin/orders" className="btn-primary">
          Kembali ke Daftar Pesanan
        </Link>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <Link to="/admin/orders" className="text-sm text-slate-500 hover:text-slate-700 mb-2 inline-block">
            ← Kembali ke Daftar Pesanan
          </Link>
          <h1 className="text-2xl font-bold text-slate-900">Detail Pesanan</h1>
        </div>
        <span className={`badge ${STATUS_CONFIG[order.status]?.className || 'badge-info'}`}>
          {STATUS_CONFIG[order.status]?.label || order.status}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Informasi Pesanan</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-slate-500">Nomor Pesanan</dt>
                <dd className="font-mono text-slate-900">{order.orderNumber}</dd>
              </div>
              <div>
                <dt className="text-sm text-slate-500">Tanggal</dt>
                <dd className="text-slate-900">
                  {new Date(order.createdAt).toLocaleString('id-ID', {
                    weekday: 'long',
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-slate-500">Outlet</dt>
                <dd className="font-medium text-slate-900">{order.outletName}</dd>
              </div>
              <div>
                <dt className="text-sm text-slate-500">Status Cetak</dt>
                <dd className="text-slate-900">{STATUS_CONFIG[order.status]?.label || order.status}</dd>
              </div>
            </dl>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Item Pesanan</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Produk</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tipe</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Qty</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Harga Satuan</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {order.items.map((item: OrderDetail['items'][0], index: number) => (
                    <tr key={index}>
                      <td className="px-4 py-3 font-medium text-slate-900">{item.productName}</td>
                      <td className="px-4 py-3 text-slate-600">{PURCHASE_TYPE_LABELS[item.purchaseType as keyof typeof PURCHASE_TYPE_LABELS] || item.purchaseType}</td>
                      <td className="px-4 py-3 text-right text-slate-900">{item.quantity}</td>
                      <td className="px-4 py-3 text-right text-slate-900">{formatRupiah(item.unitPrice)}</td>
                      <td className="px-4 py-3 text-right font-medium text-slate-900">{formatRupiah(item.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="card p-6 sticky top-24">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Ringkasan</h2>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-slate-600">Subtotal</dt>
              <dd className="text-slate-900">{formatRupiah(order.totalAmount)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-600">Total</dt>
              <dd className="text-slate-900">{formatRupiah(order.totalAmount)}</dd>
            </div>
          </dl>
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="flex justify-between text-lg font-bold">
              <span className="text-slate-900">Grand Total</span>
              <span className="text-blue-600">{formatRupiah(order.totalAmount)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}